import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    return () => el.removeEventListener('scroll', checkScroll);
  }, []);

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -320 : 320, behavior: 'smooth' });
  };

  return (
    <section className="relative bg-foreground-950 py-16 md:py-20 overflow-hidden">
      <div className="w-full px-6 md:px-10 lg:px-16 max-w-[1280px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
          <div>
            <span className="inline-block border border-white/25 text-white/80 text-xs font-medium px-3 py-1.5 rounded-full mb-4 tracking-wide">
              All Categories
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight">
              Explore Kakobuy
              <br />
              Spreadsheet Categories
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Scroll left"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white hover:bg-white/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
              aria-label="Scroll right"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {categories.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="flex-shrink-0 w-[220px] md:w-[240px] snap-start group cursor-pointer"
            >
              <div className="bg-background-50 rounded-xl overflow-hidden transition-all duration-300 group-hover:scale-[1.02]">
                <div className="aspect-square w-full overflow-hidden">
                  <img
                    src={cat.image}
                    alt={`Kakobuy spreadsheet ${cat.name}`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    title={`Kakobuy Spreadsheet ${cat.name}`}
                    loading="lazy"
                  />
                </div>
                <div className="p-4 bg-background-50">
                  <h3 className="font-semibold text-foreground-900 text-sm whitespace-nowrap">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">View Products</p>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}